/**
 * Safe subset of markdown for chat: fenced code, inline code, **bold**, *italic*, paragraphs/line breaks.
 * Fenced blocks use syntax highlighting + copy (chat-code-highlight).
 */

import { renderChatCodeBlock } from './chat-code-highlight';

function escapeHtml(s: string): string {
  if (s == null || s === '') return '';
  return String(s)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

type Part = { type: 'code'; lang: string; content: string } | { type: 'text'; content: string };

function splitFencedCode(raw: string): Part[] {
  const parts: Part[] = [];
  let i = 0;
  const str = String(raw);
  while (i < str.length) {
    const open = str.indexOf('```', i);
    if (open === -1) {
      if (i < str.length) parts.push({ type: 'text', content: str.slice(i) });
      break;
    }
    if (open > i) parts.push({ type: 'text', content: str.slice(i, open) });
    const afterTicks = open + 3;
    const lineBreak = str.indexOf('\n', afterTicks);
    const lang =
      lineBreak === -1
        ? str.slice(afterTicks).trim()
        : str.slice(afterTicks, lineBreak).trim();
    const codeStart = lineBreak === -1 ? afterTicks : lineBreak + 1;
    const close = str.indexOf('```', codeStart);
    if (close === -1) {
      parts.push({ type: 'text', content: str.slice(open) });
      break;
    }
    parts.push({ type: 'code', lang: lang.replace(/[^\w+-]/g, '').slice(0, 24), content: str.slice(codeStart, close) });
    i = close + 3;
    if (str[i] === '\n') i += 1;
  }
  return parts.length ? parts : [{ type: 'text', content: str }];
}

const PLACEHOLDER = '\uE000'; // private use — unlikely in chat

function formatTextSegment(seg: string): string {
  const slots: string[] = [];
  let s = seg;

  s = s.replace(/`([^`\n]+)`/g, (_, code: string) => {
    slots.push('<code class="rf-chat-md-inline">' + escapeHtml(code) + '</code>');
    return PLACEHOLDER + (slots.length - 1) + PLACEHOLDER;
  });

  s = s.replace(/\*\*([^*]+?)\*\*/g, (_, t: string) => {
    if (String(t).includes(PLACEHOLDER)) return '**' + t + '**';
    slots.push('<strong class="rf-chat-md-strong">' + escapeHtml(t) + '</strong>');
    return PLACEHOLDER + (slots.length - 1) + PLACEHOLDER;
  });

  s = s.replace(/(^|[^*])\*([^*\n]+?)\*(?!\*)/g, (full, pre: string, t: string) => {
    if (String(t).includes(PLACEHOLDER)) return full;
    slots.push((pre || '') + '<em class="rf-chat-md-em">' + escapeHtml(t) + '</em>');
    return PLACEHOLDER + (slots.length - 1) + PLACEHOLDER;
  });

  s = escapeHtml(s);
  for (let j = 0; j < slots.length; j += 1) {
    s = s.split(PLACEHOLDER + j + PLACEHOLDER).join(slots[j]);
  }

  const blocks = s.split(/\n\n+/);
  return blocks
    .map((block) => {
      const trimmed = block.trim();
      if (!trimmed) return '';
      return '<p class="rf-chat-md-p">' + trimmed.replace(/\n/g, '<br />') + '</p>';
    })
    .join('');
}

/**
 * Full message body → HTML (for AI / rich chat).
 */
export function formatChatMarkdown(raw: string): string {
  const parts = splitFencedCode(raw);
  return parts
    .map((p) => {
      if (p.type === 'code') {
        return renderChatCodeBlock(p.lang || 'code', p.content);
      }
      return formatTextSegment(p.content);
    })
    .join('');
}
